import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock, Plus, Trash2, Edit2, Loader2, ChevronLeft, ChevronRight,
  Calendar, Users, Timer, Play, Square, Pause, MapPin, Mail, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const getGPSLocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

const formatTimer = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatLocationLink = (loc) => {
  if (!loc || !loc.lat || !loc.lng) return null;
  const url = `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs">
      <MapPin className="w-3 h-3" /> Karte
    </a>
  );
};

const ActiveTimerCard = ({ entry, members, onClockOut, onPause }) => {
  const [elapsed, setElapsed] = useState(0);
  const isPaused = !!entry.pause_start_at;

  useEffect(() => {
    const calcElapsed = () => {
      const start = new Date(entry.clock_in_at).getTime();
      const now = Date.now();
      let totalPause = (entry.total_pause_seconds || 0) * 1000;
      if (entry.pause_start_at) {
        totalPause += now - new Date(entry.pause_start_at).getTime();
      }
      return Math.max(0, Math.floor((now - start - totalPause) / 1000));
    };
    setElapsed(calcElapsed());
    const iv = setInterval(() => setElapsed(calcElapsed()), 1000);
    return () => clearInterval(iv);
  }, [entry.clock_in_at, entry.total_pause_seconds, entry.pause_start_at]);

  return (
    <Card className={`border-2 ${isPaused ? 'border-yellow-400 bg-yellow-50' : 'border-green-400 bg-green-50'}`} data-testid={`active-timer-${entry.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`}>
              {(entry.member_name || '?')[0].toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{entry.member_name}</h3>
              <p className="text-sm text-gray-500">Start: {entry.start_time} Uhr</p>
              {entry.clock_in_location && formatLocationLink(entry.clock_in_location)}
            </div>
          </div>
          <div className="text-center">
            <div className={`font-mono text-3xl font-bold tabular-nums ${isPaused ? 'text-yellow-600' : 'text-green-700'}`} data-testid="timer-display">
              {formatTimer(elapsed)}
            </div>
            <p className="text-xs text-gray-500 mt-1">{isPaused ? 'PAUSE' : 'Arbeitet'}</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPause(entry.id)}
              className={isPaused ? 'border-green-500 text-green-600 hover:bg-green-50' : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'}
              data-testid={`pause-btn-${entry.id}`}
            >
              {isPaused ? <><Play className="w-4 h-4 mr-1" /> Weiter</> : <><Pause className="w-4 h-4 mr-1" /> Pause</>}
            </Button>
            <Button
              size="sm"
              onClick={() => onClockOut(entry.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`clockout-btn-${entry.id}`}
            >
              <Square className="w-4 h-4 mr-1" /> Ausstempeln
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminZeiterfassung = () => {
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activeEntries, setActiveEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showClockInDialog, setShowClockInDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedMember, setSelectedMember] = useState('all');
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [clockInMember, setClockInMember] = useState('');
  // Neue States: Tage auswählen und Auszahlung erfassen
  const [selectedEntryIds, setSelectedEntryIds] = useState(new Set());
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', notes: '', method: 'Überweisung' });
  const [showWageDialog, setShowWageDialog] = useState(false);
  const [wageEditMember, setWageEditMember] = useState(null);
  const [wageInput, setWageInput] = useState('');
  const [form, setForm] = useState({
    member_id: '', member_name: '', date: new Date().toISOString().split('T')[0],
    start_time: '07:00', end_time: '16:00', pause_minutes: '30', project: '', notes: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const dateFrom = `${viewYear}-${String(viewMonth).padStart(2, '0')}-01`;
    const dateTo = `${viewYear}-${String(viewMonth).padStart(2, '0')}-31`;
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (selectedMember !== 'all') params.member_id = selectedMember;
      const [membersRes, entriesRes, summaryRes, activeRes] = await Promise.all([
        api.get('/admin/team-members'),
        api.get('/admin/time-entries', { params }),
        api.get('/admin/time-entries/summary', { params: { month: viewMonth, year: viewYear } }),
        api.get('/admin/time-entries/active')
      ]);
      setMembers(membersRes.data || []);
      setEntries((entriesRes.data || []).filter(e => e.status !== 'active'));
      setSummary(summaryRes.data || []);
      setActiveEntries(activeRes.data || []);
    } catch (e) { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [viewMonth, viewYear, selectedMember]);

  useEffect(() => { loadData(); }, [loadData]);
  // Refresh active timers every 30s
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const res = await api.get('/admin/time-entries/active');
        setActiveEntries(res.data || []);
      } catch (e) { /* ignore */ }
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  const changeMonth = (dir) => {
    let m = viewMonth + dir, y = viewYear;
    if (m > 12) { m = 1; y++; } else if (m < 1) { m = 12; y--; }
    setViewMonth(m); setViewYear(y);
  };

  const handleClockIn = async () => {
    if (!clockInMember) { toast.error('Mitarbeiter wählen'); return; }
    const member = members.find(m => m.id === clockInMember);
    setClockingIn(clockInMember);
    toast.info('Standort wird ermittelt...');
    const location = await getGPSLocation();
    try {
      await api.post('/admin/time-entries/clock-in', {
        member_id: clockInMember,
        member_name: member?.name || '',
        location
      });
      toast.success(`${member?.name} eingestempelt!`);
      setShowClockInDialog(false);
      setClockInMember('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Einstempeln');
    }
    setClockingIn(null);
  };

  const handleClockOut = async (entryId) => {
    toast.info('Standort wird ermittelt...');
    const location = await getGPSLocation();
    try {
      const res = await api.post(`/admin/time-entries/${entryId}/clock-out`, { location });
      toast.success(res.data.message || 'Ausgestempelt');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ausstempeln');
    }
  };

  const handlePause = async (entryId) => {
    try {
      const res = await api.post(`/admin/time-entries/${entryId}/pause`, {});
      toast.success(res.data.message);
      // Refresh active entries
      const activeRes = await api.get('/admin/time-entries/active');
      setActiveEntries(activeRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      member_id: e.member_id || '', member_name: e.member_name || '',
      date: e.date || '', start_time: e.start_time || '07:00', end_time: e.end_time || '16:00',
      pause_minutes: String(e.pause_minutes || 0), project: e.project || '', notes: e.notes || ''
    });
    setShowDialog(true);
  };

  const openManualEntry = () => {
    setEditing(null);
    setForm({
      member_id: '', member_name: '', date: new Date().toISOString().split('T')[0],
      start_time: '07:00', end_time: '16:00', pause_minutes: '30', project: '', notes: ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.member_id || !form.date) { toast.error('Mitarbeiter und Datum sind Pflicht'); return; }
    const data = { ...form, pause_minutes: parseInt(form.pause_minutes) || 0, status: 'completed' };
    try {
      if (editing) {
        await api.put(`/admin/time-entries/${editing.id}`, data);
        toast.success('Zeit korrigiert');
      } else {
        await api.post('/admin/time-entries', data);
        toast.success('Eintrag erstellt');
      }
      setShowDialog(false);
      loadData();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Eintrag löschen?')) return;
    try { await api.delete(`/admin/time-entries/${id}`); toast.success('Gelöscht'); loadData(); }
    catch (e) { toast.error('Fehler'); }
  };

  // ---- Tage auswählen und Auszahlung erfassen ----
  const toggleEntrySelection = (id) => {
    try {
      setSelectedEntryIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    } catch (e) {
      console.error('toggle error', e);
    }
  };
  const clearSelection = () => setSelectedEntryIds(new Set());

  // Verdienst-Map – MUSS vor `selectedEarnings` deklariert sein (Temporal Dead Zone)
  const wageByMember = Object.fromEntries((members || []).map(m => [m.id, Number(m.hourly_wage) || 0]));

  // Ausgewählte Tage – nur eines Mitarbeiters gleichzeitig auszahlen
  const selectedEntries = entries.filter(e => selectedEntryIds.has(e.id));
  const selectedByMember = selectedEntries.reduce((acc, e) => {
    (acc[e.member_id] = acc[e.member_id] || []).push(e); return acc;
  }, {});
  const selectedMemberIds = Object.keys(selectedByMember);
  const selectedHours = selectedEntries.reduce((s, e) => s + (e.total_hours || 0), 0);
  const selectedEarnings = selectedEntries.reduce((s, e) => {
    const w = wageByMember[e.member_id] || 0;
    return s + (e.total_hours || 0) * w;
  }, 0);

  const openPayDialog = () => {
    if (selectedMemberIds.length === 0) { toast.error('Bitte mindestens einen Tag auswählen'); return; }
    if (selectedMemberIds.length > 1) { toast.error('Bitte nur Tage von einem Mitarbeiter auswählen'); return; }
    setPayForm({
      amount: selectedEarnings.toFixed(2),
      notes: `Auszahlung ${selectedEntries.length} Tag(e) (${selectedHours.toFixed(1)} h) im ${MONTHS[viewMonth - 1]} ${viewYear}`,
      method: 'Überweisung',
    });
    setShowPayDialog(true);
  };

  // Einzeltag-Teilzahlung: markiert nur diesen Tag und öffnet den Dialog
  const payDayDialog = (entry) => {
    setSelectedEntryIds(new Set([entry.id]));
    const wage = wageByMember[entry.member_id] || 0;
    const earned = (entry.total_hours || 0) * wage;
    setPayForm({
      amount: earned > 0 ? earned.toFixed(2) : '',
      notes: `Teilzahlung ${new Date(entry.date + 'T00:00:00').toLocaleDateString('de-DE')} · ${(entry.total_hours || 0).toFixed(1)} h`,
      method: 'Überweisung',
    });
    setShowPayDialog(true);
  };

  // Stundenlohn inline bearbeiten
  const openWageDialog = (member) => {
    setWageEditMember(member);
    setWageInput(member.hourly_wage != null ? String(member.hourly_wage) : '');
    setShowWageDialog(true);
  };
  const saveWage = async () => {
    if (!wageEditMember) return;
    const val = wageInput === '' ? null : Number(String(wageInput).replace(',', '.'));
    if (wageInput !== '' && (Number.isNaN(val) || val < 0)) {
      toast.error('Ungültiger Stundenlohn'); return;
    }
    try {
      // Nur den Lohn schicken – Backend nutzt $set, andere Mitarbeiter-Felder bleiben unberührt.
      await api.put(`/admin/team-members/${wageEditMember.id}`, { hourly_wage: val });
      toast.success('Stundenlohn gespeichert');
      setShowWageDialog(false);
      setWageEditMember(null);
      // Members-State sofort lokal aktualisieren, damit Verdienst-Anzeige sofort greift
      setMembers(prev => prev.map(m => m.id === wageEditMember.id ? { ...m, hourly_wage: val } : m));
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  const savePayment = async () => {
    const amount = parseFloat(String(payForm.amount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Ungültiger Betrag'); return; }
    const memberId = selectedMemberIds[0];
    const member = members.find(m => m.id === memberId);
    const payload = {
      employee_name: member?.name || 'Unbekannt',
      description: payForm.notes || 'Stundenauszahlung',
      amount,
      status: 'Bezahlt',
      payment_method: payForm.method,
      payment_date: new Date().toISOString().split('T')[0],
      // zusätzliche Referenz-Felder für spätere Auswertung
      member_id: memberId,
      time_entry_ids: selectedEntries.map(e => e.id),
      hours: selectedHours,
      period: `${MONTHS[viewMonth - 1]} ${viewYear}`,
    };
    try {
      await api.post('/admin/employee-payments', payload);
      // Zeit-Einträge als bezahlt markieren (editierbar bleiben laut Wunsch)
      await Promise.all(selectedEntries.map(e =>
        api.put(`/admin/time-entries/${e.id}`, { ...e, paid: true, paid_amount: (e.total_hours || 0) * (wageByMember[e.member_id] || 0), paid_at: new Date().toISOString() })
      ));
      toast.success(`Auszahlung ${amount.toFixed(2)} € erfasst`);
      setShowPayDialog(false);
      clearSelection();
      loadData();
    } catch (e) {
      toast.error('Fehler beim Speichern der Auszahlung');
    }
  };

  const calcHours = (start, end, pause) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, ((eh * 60 + em) - (sh * 60 + sm) - (pause || 0)) / 60);
  };

  const totalHours = entries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
  const totalEarnings = entries.reduce((sum, e) => {
    const w = wageByMember[e.member_id] || 0;
    return sum + (e.total_hours || 0) * w;
  }, 0);
  const activeMemberIds = activeEntries.map(e => e.member_id);
  const availableForClockIn = members.filter(m => !activeMemberIds.includes(m.id));

  // Group entries by date
  const grouped = entries.reduce((acc, e) => {
    const d = e.date || 'Unbekannt';
    if (!acc[d]) acc[d] = [];
    acc[d].push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort().reverse();

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#7c3aed] to-[#9333ea] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="zeiterfassung-title">
                <Clock className="w-8 h-8" /> Stempeluhr
              </h1>
              <p className="text-white/80 mt-1">Zeiten erfassen, Löhne berechnen und Mitarbeiter auszahlen</p>
            </div>
            <div className="flex gap-2">
              <Button className="bg-white text-[#7c3aed] hover:bg-gray-100" onClick={() => setShowClockInDialog(true)} data-testid="clock-in-btn">
                <Play className="w-4 h-4 mr-2" /> Einstempeln
              </Button>
              <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" onClick={openManualEntry} data-testid="manual-entry-btn">
                <Plus className="w-4 h-4 mr-2" /> Manuell
              </Button>
              {selectedEntryIds.size > 0 && (
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg"
                  onClick={openPayDialog}
                  data-testid="pay-selected-btn"
                >
                  <Euro className="w-4 h-4 mr-2" />
                  {selectedEntryIds.size} auszahlen ({selectedEarnings.toFixed(2)} €)
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Timers Section */}
      {activeEntries.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            Aktuell eingestempelt ({activeEntries.length})
          </h2>
          <div className="space-y-3">
            {activeEntries.map(entry => (
              <ActiveTimerCard key={entry.id} entry={entry} members={members} onClockOut={handleClockOut} onPause={handlePause} />
            ))}
          </div>
        </div>
      )}

      {/* Month Navigation */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center justify-between bg-white rounded-lg border p-3">
          <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}><ChevronLeft className="w-5 h-5" /></Button>
          <p className="text-lg font-semibold text-gray-900">{MONTHS[viewMonth - 1]} {viewYear}</p>
          <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-6xl mx-auto mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center"><Timer className="w-6 h-6 text-purple-600" /></div>
            <div><p className="text-sm text-gray-500">Stunden gesamt</p><p className="text-2xl font-bold text-purple-600">{totalHours.toFixed(1)} h</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center"><Calendar className="w-6 h-6 text-blue-600" /></div>
            <div><p className="text-sm text-gray-500">Arbeitstage</p><p className="text-2xl font-bold text-blue-600">{sortedDates.length}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Users className="w-6 h-6 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Mitarbeiter</p><p className="text-2xl font-bold text-green-600">{summary.length}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center"><Euro className="w-6 h-6 text-amber-600" /></div>
            <div><p className="text-sm text-gray-500">Lohn gesamt</p><p className="text-2xl font-bold text-amber-600" data-testid="total-earnings">{totalEarnings.toFixed(2)} €</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Member Summary */}
      {summary.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Mitarbeiter-Übersicht {MONTHS[viewMonth - 1]}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {summary.map(s => {
                  const wage = wageByMember[s.member_id] || 0;
                  const earnings = (s.total_hours || 0) * wage;
                  const memberObj = members.find(m => m.id === s.member_id) || { id: s.member_id, name: s.member_name };
                  return (
                    <div key={s.member_id}
                      className={`p-3 rounded-lg border transition-all ${selectedMember === s.member_id ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid={`member-summary-${s.member_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className="font-medium cursor-pointer flex-1"
                          onClick={() => setSelectedMember(selectedMember === s.member_id ? 'all' : s.member_id)}
                        >{s.member_name}</p>
                        <button
                          onClick={(ev) => { ev.stopPropagation(); openWageDialog(memberObj); }}
                          className="text-xs text-purple-600 hover:text-purple-800 hover:underline whitespace-nowrap"
                          data-testid={`edit-wage-${s.member_id}`}
                          title="Stundenlohn setzen"
                        >
                          <Edit2 className="w-3 h-3 inline mr-0.5" />Lohn
                        </button>
                      </div>
                      <div className="flex justify-between mt-1 text-sm cursor-pointer" onClick={() => setSelectedMember(selectedMember === s.member_id ? 'all' : s.member_id)}>
                        <span className="text-purple-600">{s.total_hours.toFixed(1)} h</span>
                        <span className="text-gray-400">{s.days} Tage</span>
                      </div>
                      <div className="flex justify-between mt-1 text-sm border-t pt-1">
                        {wage > 0 ? (
                          <>
                            <span className="text-gray-500">{wage.toFixed(2)} €/Std.</span>
                            <span className="font-semibold text-amber-600" data-testid={`member-earnings-${s.member_id}`}>= {earnings.toFixed(2)} €</span>
                          </>
                        ) : (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); openWageDialog(memberObj); }}
                            className="text-xs text-amber-600 hover:text-amber-800 hover:underline italic"
                          >
                            Stundenlohn hier hinterlegen →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="max-w-6xl mx-auto mb-4 flex gap-3">
        <Select value={selectedMember} onValueChange={setSelectedMember}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Alle Mitarbeiter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Mitarbeiter</SelectItem>
            {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Time Entries */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
        ) : sortedDates.length === 0 ? (
          <Card><CardContent className="text-center py-8 text-gray-500"><Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Keine Einträge in diesem Monat</p></CardContent></Card>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(date => (
              <Card key={date}>
                <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {new Date(date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {grouped[date].map(entry => {
                    const wage = wageByMember[entry.member_id] || 0;
                    const dayEarnings = (entry.total_hours || 0) * wage;
                    const isSel = selectedEntryIds.has(entry.id);
                    const isPaid = !!entry.paid;
                    return (
                    <div key={entry.id} className={`flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 border-b last:border-0 ${isSel ? 'bg-amber-50' : ''} ${isPaid ? 'bg-green-50/40' : ''}`} data-testid={`time-entry-${entry.id}`}>
                      <label className="flex items-start gap-3 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleEntrySelection(entry.id)}
                          className="mt-1.5 w-5 h-5 accent-amber-600 flex-shrink-0"
                          data-testid={`select-entry-${entry.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-gray-900">{entry.member_name}</span>
                            {entry.project && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{entry.project}</span>}
                            {isPaid && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">✓ Bezahlt</span>}
                          </div>
                          <p className="text-sm text-gray-500">
                            {entry.start_time} - {entry.end_time || '???'} ({entry.pause_minutes || 0} Min. Pause)
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            {entry.clock_in_location && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-green-500" /> Ein: {formatLocationLink(entry.clock_in_location)}
                              </span>
                            )}
                            {entry.clock_out_location && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-red-500" /> Aus: {formatLocationLink(entry.clock_out_location)}
                              </span>
                            )}
                          </div>
                          {entry.notes && <p className="text-xs text-gray-400 mt-1">{entry.notes}</p>}
                        </div>
                      </label>
                      <div className="flex items-center gap-3 mt-2 sm:mt-0 flex-wrap">
                        <span className="text-lg font-bold text-purple-600">{(entry.total_hours || 0).toFixed(1)} h</span>
                        {wage > 0 && (
                          <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">
                            = {dayEarnings.toFixed(2)} €
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => payDayDialog(entry)}
                          className="border-amber-400 text-amber-700 hover:bg-amber-50 hidden sm:inline-flex"
                          data-testid={`pay-day-${entry.id}`}
                          title="Teilzahlung für diesen Tag"
                        >
                          <Euro className="w-3.5 h-3.5 mr-1" /> Teilzahlung
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(entry)} title="Zeit korrigieren" data-testid={`edit-entry-${entry.id}`}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Clock-In Dialog */}
      <Dialog open={showClockInDialog} onOpenChange={setShowClockInDialog}>        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Play className="w-5 h-5 text-green-600" /> Einstempeln</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Mitarbeiter wählen *</Label>
              <Select value={clockInMember || undefined} onValueChange={setClockInMember}>
                <SelectTrigger className="mt-1" data-testid="select-clockin-member"><SelectValue placeholder="Mitarbeiter..." /></SelectTrigger>
                <SelectContent>{availableForClockIn.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <MapPin className="w-4 h-4 inline mr-1" /> Der Standort wird automatisch beim Einstempeln erfasst.
            </div>
            <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-600">
              <Mail className="w-4 h-4 inline mr-1" /> Sie erhalten eine E-Mail-Benachrichtigung.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockInDialog(false)}>Abbrechen</Button>
            <Button onClick={handleClockIn} disabled={!!clockingIn || !clockInMember} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-clockin-btn">
              {clockingIn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Jetzt einstempeln
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / Manual Entry Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Zeit korrigieren' : 'Arbeitszeit manuell erfassen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={form.member_id || undefined} onValueChange={(v) => { const m = members.find(x => x.id === v); setForm(p => ({ ...p, member_id: v, member_name: m?.name || '' })); }}>
                <SelectTrigger className="mt-1" data-testid="select-member"><SelectValue placeholder="Mitarbeiter wählen..." /></SelectTrigger>
                <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Datum *</Label><Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="input-date" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Beginn</Label><Input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} className="mt-1" /></div>
              <div><Label>Ende</Label><Input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} className="mt-1" /></div>
              <div><Label>Pause (Min)</Label><Input type="number" value={form.pause_minutes} onChange={(e) => setForm(p => ({ ...p, pause_minutes: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 text-center text-sm font-medium text-purple-700">
              = {calcHours(form.start_time, form.end_time, parseInt(form.pause_minutes) || 0).toFixed(1)} Stunden
            </div>
            <div><Label>Projekt / Baustelle</Label><Input value={form.project} onChange={(e) => setForm(p => ({ ...p, project: e.target.value }))} className="mt-1" /></div>
            <div><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="save-entry-btn">
              {editing ? 'Korrigieren' : 'Erfassen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auszahlungs-Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-amber-600" /> Auszahlung erfassen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="font-semibold text-amber-900">
                {selectedMemberIds.length === 1 ? members.find(m => m.id === selectedMemberIds[0])?.name : ''}
              </p>
              <p className="text-amber-800 mt-1">
                {selectedEntries.length} Tag(e) · {selectedHours.toFixed(1)} Stunden · vorgeschlagen: <strong>{selectedEarnings.toFixed(2)} €</strong>
              </p>
            </div>
            <div>
              <Label>Zu zahlender Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={payForm.amount}
                onChange={(e) => setPayForm(p => ({ ...p, amount: e.target.value }))}
                className="mt-1 text-lg font-semibold"
                data-testid="pay-amount-input"
              />
              <p className="text-xs text-gray-500 mt-1">Abweichungen wie Bonus, Vorschuss oder Abzug einfach überschreiben.</p>
            </div>
            <div>
              <Label>Zahlungsart</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm(p => ({ ...p, method: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Überweisung">Überweisung</SelectItem>
                  <SelectItem value="Bar">Bar</SelectItem>
                  <SelectItem value="EC-Karte">EC-Karte</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz</Label>
              <Input
                value={payForm.notes}
                onChange={(e) => setPayForm(p => ({ ...p, notes: e.target.value }))}
                className="mt-1"
                data-testid="pay-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>Abbrechen</Button>
            <Button onClick={savePayment} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-pay-btn">
              <Euro className="w-4 h-4 mr-2" /> Auszahlung erfassen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Stundenlohn-Dialog */}
      <Dialog open={showWageDialog} onOpenChange={setShowWageDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-purple-600" /> Stundenlohn
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-semibold text-purple-900">{wageEditMember?.name || ''}</p>
              <p className="text-purple-800 mt-0.5 text-xs">Aktueller Stundenlohn wird für alle Zeit-Einträge dieses Mitarbeiters verwendet.</p>
            </div>
            <div>
              <Label>Stundenlohn (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                autoFocus
                value={wageInput}
                onChange={(e) => setWageInput(e.target.value)}
                placeholder="z.B. 18.50"
                className="mt-1 text-lg font-semibold"
                data-testid="wage-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWageDialog(false)}>Abbrechen</Button>
            <Button onClick={saveWage} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-wage-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminZeiterfassung;
